import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverTrigger,
} from "@/components/ui/popover";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Slider } from "@/components/ui/slider";
import { cn } from '@/lib/utils';
import { BubbleMenu } from '@tiptap/react';
import { forwardRef, useState, useEffect } from 'react';
import {
    MdDelete,
    MdFormatAlignCenter,
    MdFormatAlignLeft,
    MdFormatAlignRight,
    MdFormatClear,
    MdKeyboardArrowDown,
    MdOutlineCode,
    MdOutlineFormatBold,
    MdOutlineFormatItalic,
    MdOutlineFormatListBulleted,
    MdOutlineFormatListNumbered,
    MdOutlineFormatQuote,
    MdOutlineFormatStrikethrough,
    MdOutlineFormatUnderlined,
    MdOutlineHorizontalRule,
    MdOutlineLink,
    MdOutlineLinkOff,
    MdOutlineContentCopy,
    MdOutlineLaunch,
} from 'react-icons/md';
import LinkInput from "./LinkInput";


const InlinePopoverContent = forwardRef(({ className, align = "start", sideOffset = 8, ...props }, ref) => (
  <PopoverPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
InlinePopoverContent.displayName = "InlinePopoverContent";

const MenuButton = ({ onClick, active, disabled, children, tooltip, className }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={(e) => {
      e.preventDefault();
      onClick();
    }}
    disabled={disabled}
    className={cn(
      "h-8 px-2 hover:bg-accent/50 transition-colors relative group",
      active && "bg-accent/50 text-accent-foreground",
      className
    )}
  >
    {children}
    {tooltip && (
      <div className="absolute hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 px-2 py-1 text-xs bg-popover text-popover-foreground rounded shadow-md whitespace-nowrap mb-2 border">
        <div className="font-medium mb-0.5">{tooltip.title}</div>
        {tooltip.shortcut && (
          <div className="text-muted-foreground">{tooltip.shortcut}</div>
        )}
      </div>
    )}
  </Button>
);

const headingOptions = [
  { level: 0, label: 'Normal text', shortcut: '⌘+Alt+0' },
  { level: 1, label: 'Heading 1', shortcut: '# + space' },
  { level: 2, label: 'Heading 2', shortcut: '## + space' },
  { level: 3, label: 'Heading 3', shortcut: '### + space' },
];

const codeLanguages = [
  { value: 'plaintext', label: 'Plain Text' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'json', label: 'JSON' },
  { value: 'sql', label: 'SQL' },
  { value: 'bash', label: 'Bash' },
];

const ImageMenu = ({ editor }) => {
  const getCurrentSize = () => {
    if (editor.isActive('s3Image', { size: 'small' })) return 25;
    if (editor.isActive('s3Image', { size: 'medium' })) return 50;
    if (editor.isActive('s3Image', { size: 'large' })) return 75;
    if (editor.isActive('s3Image', { size: 'full' })) return 100;
    return 100;
  };

  const [currentSize, setCurrentSize] = useState(getCurrentSize());

  useEffect(() => {
    setCurrentSize(getCurrentSize());
  }, [editor.state.selection]);

  const handleSizeChange = (value) => {
    const sizeMap = {
      25: 'small',
      50: 'medium',
      75: 'large',
      100: 'full'
    };
    setCurrentSize(value[0]);
    editor.chain().focus().updateAttributes('s3Image', { size: sizeMap[value[0]] }).run();
  };

  return (
    <div className="flex items-center px-3 space-x-2 min-w-[200px]">
      <div className="flex space-x-1">
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          tooltip={{ title: 'Align left' }}
        >
          <MdFormatAlignLeft className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          tooltip={{ title: 'Align center' }}
        >
          <MdFormatAlignCenter className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          tooltip={{ title: 'Align right' }}
        >
          <MdFormatAlignRight className="h-4 w-4" />
        </MenuButton>
      </div>
      <div className="h-4 w-px bg-border mx-2" />
      <Slider
        value={[currentSize]}
        max={100}
        min={25}
        step={25}
        onValueChange={handleSizeChange}
        className="w-32"
      />
      <span className="text-sm text-muted-foreground min-w-[48px]">
        {currentSize}%
      </span>
      <div className="h-4 w-px bg-border mx-2" />
      <MenuButton
        onClick={() => editor.chain().focus().deleteSelection().run()}
        tooltip={{ title: 'Delete image' }}
      >
        <MdDelete className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

const CodeBlockMenu = ({ editor, showCodeMenu, setShowCodeMenu }) => (
  <div className="flex items-center px-1.5 space-x-1">
    <Popover open={showCodeMenu} onOpenChange={setShowCodeMenu}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-8 px-2 hover:bg-accent/50 transition-colors relative group flex items-center gap-1",
            editor.isActive('codeBlock') && "bg-accent/50 text-accent-foreground"
          )}
        >
          <MdOutlineCode className="h-4 w-4" />
          <MdKeyboardArrowDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <InlinePopoverContent className="w-48 p-1" sideOffset={8} align="start">
        <div className="flex flex-col space-y-0.5">
          {codeLanguages.map((lang) => (
            <Button
              key={lang.value}
              variant={editor.isActive('codeBlock', { language: lang.value }) ? 'secondary' : 'ghost'}
              className="flex items-center justify-between px-2 py-1 text-sm"
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().setCodeBlock({ language: lang.value }).run();
                setShowCodeMenu(false);
              }}
            >
              <span>{lang.label}</span>
            </Button>
          ))}
        </div>
      </InlinePopoverContent>
    </Popover>
    <MenuButton
      onClick={() => editor.chain().focus().setParagraph().run()}
      tooltip={{ title: 'Convert to text' }}
    >
      <MdFormatClear className="h-4 w-4" />
    </MenuButton>
  </div>
);

const LinkMenu = ({ editor, setIsLinkInputOpen }) => {
  const [isCopied, setIsCopied] = useState(false);
  return (
    <div className="flex items-center px-1.5 space-x-1">
      <>
        <MenuButton
          onClick={() => {
            const link = editor.getAttributes('link').href;
            window.open(link, '_blank');
          }}
          tooltip={{ title: 'Go to link' }}
        >
          <MdOutlineLaunch className="h-3.5 w-3.5" />
        </MenuButton>
        <MenuButton
          onClick={() => {
            navigator.clipboard.writeText(editor.getAttributes('link').href);
            setIsCopied(true);
            setTimeout(() => {
              setIsCopied(false);
            }, 2000);
          }}
          tooltip={{ title: isCopied ? 'Copied!' : 'Copy link' }}
        >
          <MdOutlineContentCopy className="h-3 w-3" />
        </MenuButton>
        <div className="h-4 w-px bg-border mx-2" />
        <MenuButton
          onClick={() => setIsLinkInputOpen(true)}
          tooltip={{ title: 'Edit link' }}
        >
          <MdOutlineLink className="h-4 w-4" />
        </MenuButton>
        <MenuButton
          onClick={() => {
            editor.chain().focus().unsetLink().run();
          }}
          tooltip={{ title: 'Unlink' }}
        >
          <MdOutlineLinkOff className="h-4 w-4" />
        </MenuButton>
      </>
    </div>
  )
}

const hasLinkInSelection = (state, from, to) => {
  let hasLink = false;
  state.doc.nodesBetween(from, to, (node) => {
    if (node.marks?.some(mark => mark.type.name === 'link')) {
      hasLink = true;
      return false; // Stop traversing
    }
  });
  return hasLink;
};


export default function EditorBubbleMenu({ editor }) {
  if (!editor) return null;

  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const [showCodeMenu, setShowCodeMenu] = useState(false);
  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);
  // Test if heading commands are available
  const canToggleHeading = (level) => {
    try {
      return editor.can().toggleHeading({ level });
    } catch (e) {
      console.error('Heading error:', e);
      return false;
    }
  };

  const basicItems = [
    {
      icon: <MdOutlineFormatBold className="h-4 w-4" />,
      title: 'Bold',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
      tooltip: { title: 'Bold', shortcut: '⌘+B' },
    },
    {
      icon: <MdOutlineFormatItalic className="h-4 w-4" />,
      title: 'Italic',
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
      tooltip: { title: 'Italic', shortcut: '⌘+I' },
    },
    {
      icon: <MdOutlineFormatUnderlined className="h-4 w-4" />,
      title: 'Underline',
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: () => editor.isActive('underline'),
      tooltip: { title: 'Underline', shortcut: '⌘+U' },
    },
    {
      icon: <MdOutlineFormatStrikethrough className="h-4 w-4" />,
      title: 'Strike',
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
      tooltip: { title: 'Strikethrough' },
    },
  ];

  // Helper function to get current heading text
  const getCurrentHeadingText = () => {
    if (editor.isActive('heading', { level: 1 })) return 'Heading 1';
    if (editor.isActive('heading', { level: 2 })) return 'Heading 2';
    if (editor.isActive('heading', { level: 3 })) return 'Heading 3';
    return 'Normal text';
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        popperOptions: {
          placement: 'top-start',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
              },
            },
          ],
        },
        maxWidth: 'calc(100vw - 16px)',
      }}
      shouldShow={({ editor, state, from, to, view }) => {
        // Don't show menu if editor isn't editable
        if (!editor.isEditable) return false;

        // Don't show menu if editor isn't focused
        if (!view.hasFocus()) return false;

        // Get the selected node
        const { selection } = state;
        const node = selection.$anchor.node();

        // If nothing is selected, don't show the menu
        if (!editor.isActive('link') && from === to) return false;

        // If an S3Image is selected
        if (editor.isActive('s3Image')) {
          return true;
        }

        // If a code block is selected
        if (editor.isActive('codeBlock')) {
          return true;
        }

        if (editor.isActive('link')) {
          return true;
        }

        // For regular text selection
        return true;
      }}
      updateDelay={100}
      className="flex rounded-lg border bg-background shadow-lg animate-in fade-in-0 zoom-in-95 py-1.5"
    >
      <div className="flex items-center divide-x divide-border">
        {editor.isActive('s3Image') ? (
          <ImageMenu editor={editor} />
        ) : editor.isActive('codeBlock') ? (
          <CodeBlockMenu
            editor={editor}
            showCodeMenu={showCodeMenu}
            setShowCodeMenu={setShowCodeMenu}
          />
          ) : editor.isActive('link') ? (
          <LinkMenu editor={editor} setIsLinkInputOpen={setIsLinkInputOpen} />
        ) : (
          // Your existing text formatting menu
          <>
            {/* Heading Menu */}
            <div className="flex items-center px-1.5">
              <Popover open={showHeadingMenu} onOpenChange={setShowHeadingMenu}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-8 px-2 hover:bg-accent/50 transition-colors relative group flex items-center gap-1",
                      editor.isActive('heading') && "bg-accent/50 text-accent-foreground"
                    )}
                  >
                    <span className="text-sm">{getCurrentHeadingText()}</span>
                    <MdKeyboardArrowDown className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <InlinePopoverContent
                  className="w-48 p-1"
                  sideOffset={8}
                  align="start"
                >
                  <div className="flex flex-col space-y-0.5">
                    {headingOptions.map((item) => {
                      const isActive = item.level === 0
                        ? !editor.isActive('heading')
                        : editor.isActive('heading', { level: item.level });
                      return (
                        <Button
                          key={item.level}
                          variant={isActive ? 'secondary' : 'ghost'}
                          className="flex items-center justify-between px-2 py-1 text-sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (item.level === 0) {
                              editor.chain().focus().setParagraph().run();
                            } else {
                              editor.chain().focus().setHeading({ level: item.level }).run();
                            }
                            setShowHeadingMenu(false);
                          }}
                        >
                          <span>{item.label}</span>
                          <span className="text-xs text-muted-foreground">{item.shortcut}</span>
                        </Button>
                      );
                    })}
                  </div>
                </InlinePopoverContent>
              </Popover>
            </div>

            {/* Basic Formatting */}
            <div className="flex items-center px-1.5 space-x-1">
              {basicItems.map((item) => (
                <MenuButton
                  key={item.title}
                  onClick={item.action}
                  active={item.isActive()}
                  disabled={!editor.isEditable}
                  tooltip={item.tooltip}
                >
                  {item.icon}
                </MenuButton>
              ))}
              <MenuButton
                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                tooltip={{ title: 'Clear formatting' }}
              >
                <MdFormatClear className="h-4 w-4" />
              </MenuButton>
            </div>

            {/* Quote */}
            <div className="flex items-center px-1.5">
              <MenuButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                active={editor.isActive('blockquote')}
                tooltip={{ title: 'Quote', shortcut: '> + space' }}
              >
                <MdOutlineFormatQuote className="h-4 w-4" />
              </MenuButton>
            </div>

            {/* Lists */}
            <div className="flex items-center px-1.5 space-x-1">
              <MenuButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editor.isActive('bulletList')}
                tooltip={{ title: 'Bullet List', shortcut: '* + space' }}
              >
                <MdOutlineFormatListBulleted className="h-4 w-4" />
              </MenuButton>
              <MenuButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editor.isActive('orderedList')}
                tooltip={{ title: 'Numbered List', shortcut: '1. + space' }}
              >
                <MdOutlineFormatListNumbered className="h-4 w-4" />
              </MenuButton>
            </div>

            {/* Links */}
            <div className="flex items-center px-1.5 space-x-1">
              {hasLinkInSelection(editor.state, editor.state.selection.from, editor.state.selection.to) ? (
                <>
                  <MenuButton
                    onClick={() => {
                      const link = editor.getAttributes('link').href;
                      window.open(link, '_blank');
                    }}
                    tooltip={{ title: 'Go to link' }}
                  >
                    <MdOutlineLaunch className="h-3.5 w-3.5" />
                  </MenuButton>
                  <MenuButton
                    onClick={() => setIsLinkInputOpen(true)}
                    tooltip={{ title: 'Edit link' }}
                  >
                    <MdOutlineLink className="h-4 w-4" />
                  </MenuButton>
                  <MenuButton
                    onClick={() => {
                      editor.chain().focus().unsetLink().run();
                    }}
                    tooltip={{ title: 'Remove link' }}
                  >
                    <MdOutlineLinkOff className="h-4 w-4" />
                  </MenuButton>
                </>
              ) : (
                <MenuButton
                  onClick={() => setIsLinkInputOpen(true)}
                  tooltip={{ title: 'Add link' }}
                >
                  <MdOutlineLink className="h-4 w-4" />
                </MenuButton>
              )}
            </div>

            {/* Code Block and Horizontal Rule */}
            <div className="flex items-center px-1.5 space-x-1">
              <Popover open={showCodeMenu} onOpenChange={setShowCodeMenu}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-8 px-2 hover:bg-accent/50 transition-colors relative group flex items-center gap-1",
                      editor.isActive('codeBlock') && "bg-accent/50 text-accent-foreground"
                    )}
                  >
                    <MdOutlineCode className="h-4 w-4" />
                    <MdKeyboardArrowDown className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <InlinePopoverContent
                  className="w-48 p-1"
                  sideOffset={8}
                  align="start"
                >
                  <div className="flex flex-col space-y-0.5">
                    {codeLanguages.map((lang) => (
                      <Button
                        key={lang.value}
                        variant={editor.isActive('codeBlock', { language: lang.value }) ? 'secondary' : 'ghost'}
                        className="flex items-center justify-between px-2 py-1 text-sm"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          editor.chain().focus().setCodeBlock({ language: lang.value }).run();
                          setShowCodeMenu(false);
                        }}
                      >
                        <span>{lang.label}</span>
                      </Button>
                    ))}
                  </div>
                </InlinePopoverContent>
              </Popover>

              <MenuButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                tooltip={{ title: 'Horizontal Rule', shortcut: '--- + space' }}
              >
                <MdOutlineHorizontalRule className="h-4 w-4" />
              </MenuButton>
            </div>
          </>
        )}
      </div>
      {isLinkInputOpen && (
        <LinkInput
          editLinkInput={editor.getAttributes('link').href}
          editor={editor}
          isLinkInputOpen={isLinkInputOpen}
          setIsLinkInputOpen={setIsLinkInputOpen}
        />
      )}
    </BubbleMenu>
  );
}
